import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Group,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconLock } from '@tabler/icons-react';
import { AppModal } from '../../shared/components/AppModal';
import { ApiError } from '../../shared/api/problem';
import {
  TRAN_CODE_MAX_LENGTH,
  createTransactionCode,
  deleteTransactionCode,
  listTransactionCodeGroups,
  listTransactionCodeTypes,
  updateTransactionCode,
  type TransactionCodeDto,
} from './api';

interface Props {
  opened: boolean;
  onClose: () => void;
  /** Null to create. */
  code: TransactionCodeDto | null;
  onSaved: () => void;
}

interface FormState {
  tranCode: string;
  displayName: string;
  transactionCodeTypeId: string;
  transactionCodeGroupId: string;
  isActive: boolean;
}

/**
 * Creates or edits one transaction code.
 *
 * For a **system** code — which every seeded one is — the code, its effect and its group are shown
 * read-only, and it cannot be deactivated or deleted. Only the description is editable. The server
 * refuses the rest anyway (53510 / 53511 / 53512); disabling the fields means nobody types a change
 * that was never going to be accepted.
 *
 * Deleting is offered only for a code nobody has posted with. Once it has history, deactivating is
 * the answer: the code leaves every picker and its past transactions still read correctly.
 */
export function TransactionCodeFormModal({ opened, onClose, code, onSaved }: Props) {
  const { t } = useTranslation();
  const isEdit = code !== null;
  const isLocked = code?.isLocked ?? false;

  const types = useQuery({
    queryKey: ['transaction-code-types'] as const,
    queryFn: ({ signal }) => listTransactionCodeTypes(signal),
    enabled: opened,
  });

  const groups = useQuery({
    queryKey: ['transaction-code-groups'] as const,
    queryFn: ({ signal }) => listTransactionCodeGroups(signal),
    enabled: opened,
  });

  const [form, setForm] = useState<FormState>({
    tranCode: '',
    displayName: '',
    transactionCodeTypeId: '',
    transactionCodeGroupId: '',
    isActive: true,
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!opened) return;
    setErrors({});
    setSubmitError(null);
    setForm({
      tranCode: code?.tranCode ?? '',
      displayName: code?.displayName ?? '',
      transactionCodeTypeId: code ? String(code.transactionCodeTypeId) : '',
      transactionCodeGroupId: code?.transactionCodeGroupId ?? '',
      isActive: code?.isActive ?? true,
    });
  }, [opened, code]);

  const typeOptions = useMemo(
    () =>
      (types.data ?? []).map((ty) => ({
        value: String(ty.transactionCodeTypeId),
        label: ty.displayName,
      })),
    [types.data],
  );

  const groupOptions = useMemo(
    () =>
      (groups.data ?? []).map((g) => ({
        value: g.transactionCodeGroupId,
        label: `${g.groupCode} — ${g.displayName}`,
      })),
    [groups.data],
  );

  const save = useMutation({
    mutationFn: () => {
      const body = {
        tranCode: form.tranCode.trim().toUpperCase(),
        displayName: form.displayName.trim(),
        transactionCodeTypeId: Number(form.transactionCodeTypeId),
        transactionCodeGroupId: form.transactionCodeGroupId,
        isActive: form.isActive,
      };
      return isEdit ? updateTransactionCode(code!.transactionCodeId, body) : createTransactionCode(body);
    },
    onSuccess: () => {
      notifications.show({ message: t('transactionCodes.saved'), color: 'green' });
      onSaved();
      onClose();
    },
    onError: (err) => setSubmitError(describe(err)),
  });

  const remove = useMutation({
    mutationFn: () => deleteTransactionCode(code!.transactionCodeId),
    onSuccess: () => {
      notifications.show({ message: t('transactionCodes.deleted'), color: 'green' });
      onSaved();
      onClose();
    },
    onError: (err) => setSubmitError(describe(err)),
  });

  /** The server's own sentence when it has one: each refusal says what to do instead. */
  function describe(err: unknown): string {
    if (err instanceof ApiError) {
      return err.problem.detail ?? err.problem.title ?? t('transactionCodes.errors.saveFailed');
    }
    return t('transactionCodes.errors.saveFailed');
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);

    const next: typeof errors = {};
    const trimmed = form.tranCode.trim();
    if (!trimmed) {
      next.tranCode = t('transactionCodes.errors.codeRequired');
    } else if (trimmed.length > TRAN_CODE_MAX_LENGTH) {
      // Checked here as well as server-side because the column is varchar(3): a longer value would
      // be truncated rather than refused, so the user has to be told before they submit.
      next.tranCode = t('transactionCodes.errors.codeTooLong', { max: TRAN_CODE_MAX_LENGTH });
    }
    if (!form.displayName.trim()) next.displayName = t('transactionCodes.errors.descriptionRequired');
    if (!form.transactionCodeTypeId) next.transactionCodeTypeId = t('transactionCodes.errors.effectRequired');
    if (!form.transactionCodeGroupId) next.transactionCodeGroupId = t('transactionCodes.errors.groupRequired');

    setErrors(next);
    if (Object.keys(next).length > 0) return;
    save.mutate();
  }

  const canDelete = isEdit && !isLocked && !code!.isInUse;

  return (
    <AppModal
      opened={opened}
      onClose={onClose}
      title={isEdit ? t('transactionCodes.editTitle', { code: code!.tranCode }) : t('transactionCodes.newTitle')}
      width="md"
    >
      <form onSubmit={onSubmit} noValidate>
        <Stack gap="md">
          {isLocked && (
            <Alert color="blue" variant="light" icon={<IconLock size={16} />}>
              {t('transactionCodes.lockedExplain')}
            </Alert>
          )}

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <TextInput
              label={t('transactionCodes.code')}
              description={t('transactionCodes.codeHint', { max: TRAN_CODE_MAX_LENGTH })}
              value={form.tranCode}
              onChange={(e) => setForm({ ...form, tranCode: e.currentTarget.value.toUpperCase() })}
              error={errors.tranCode}
              maxLength={TRAN_CODE_MAX_LENGTH}
              disabled={isLocked}
              required
              autoFocus={!isEdit}
            />
            <TextInput
              label={t('transactionCodes.description')}
              value={form.displayName}
              onChange={(e) => setForm({ ...form, displayName: e.currentTarget.value })}
              error={errors.displayName}
              required
              autoFocus={isEdit}
            />
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <Select
              label={t('transactionCodes.effect')}
              description={t('transactionCodes.effectHint')}
              data={typeOptions}
              value={form.transactionCodeTypeId || null}
              onChange={(v) => setForm({ ...form, transactionCodeTypeId: v ?? '' })}
              error={errors.transactionCodeTypeId}
              disabled={isLocked || types.isLoading}
              required
            />
            <Select
              label={t('transactionCodes.group')}
              description={t('transactionCodes.groupHint')}
              data={groupOptions}
              value={form.transactionCodeGroupId || null}
              onChange={(v) => setForm({ ...form, transactionCodeGroupId: v ?? '' })}
              error={errors.transactionCodeGroupId}
              disabled={isLocked || groups.isLoading}
              required
            />
          </SimpleGrid>

          <Switch
            label={t('transactionCodes.active')}
            description={
              isLocked
                ? t('transactionCodes.activeLockedHint')
                : t('transactionCodes.activeHint')
            }
            checked={form.isActive}
            onChange={(e) => setForm({ ...form, isActive: e.currentTarget.checked })}
            disabled={isLocked}
          />

          {isEdit && !canDelete && !isLocked && (
            <Text size="xs" c="dimmed">
              {t('transactionCodes.inUseHint')}
            </Text>
          )}

          {submitError && (
            <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
              {submitError}
            </Alert>
          )}

          <Group justify="space-between">
            {canDelete ? (
              <Button
                variant="light"
                color="red"
                onClick={() => remove.mutate()}
                loading={remove.isPending}
              >
                {t('transactionCodes.delete')}
              </Button>
            ) : (
              <span />
            )}
            <Group>
              <Button variant="default" onClick={onClose}>
                {t('shell.cancel')}
              </Button>
              <Button type="submit" color="invetrak" loading={save.isPending}>
                {t('shell.saveChanges')}
              </Button>
            </Group>
          </Group>
        </Stack>
      </form>
    </AppModal>
  );
}
